import {
  defaultLocale,
  isSupportedLocale,
  localeCookieName,
  type PortalLocale,
} from "@/lib/i18n/portal";

/**
 * Where the seller's language choice lives between visits.
 *
 * The seller portal takes its locale from the URL segment and nothing else, so
 * without this a choice survived only as long as the seller stayed on `/en/…`
 * links: opening the portal at the bare host always resolved to the default,
 * and an English-preferring seller had to re-switch every session.
 *
 * The same cookie name the buyer app already reads (`a2z-lang`), so a seller
 * browsing their own storefront gets the language they picked in the portal.
 * Cookie rather than localStorage for exactly that reason — it is the one store
 * both a SPA and the buyer's server rendering can see.
 */
export function readStoredLocale(): PortalLocale | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${localeCookieName}=([^;]*)`),
  );
  const value = match?.[1] ? decodeURIComponent(match[1]) : "";
  return isSupportedLocale(value) ? value : null;
}

/** Called by every language switcher, so the choice outlives the URL. */
export function storeLocale(locale: PortalLocale): void {
  if (typeof document === "undefined") return;
  // One year: a language preference is not session state.
  document.cookie = `${localeCookieName}=${encodeURIComponent(locale)}; path=/; max-age=31536000; samesite=lax`;
}

/**
 * Locale for an entry point that carries none (`/`, or an unmatched path).
 * A first visit — no cookie — gets `defaultLocale`, which is Arabic.
 */
export function resolveEntryLocale(): PortalLocale {
  return readStoredLocale() ?? defaultLocale;
}
